// V8.1 · Saúde Financeira · bloco MATRÍCULAS & REMATRÍCULAS (nada de resíduo escondido).
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, Trash2, RotateCcw, ExternalLink, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import PurgeBulkDialog from './PurgeBulkDialog';

const VIEWS = [
  { key: 'ativos', label: 'Ativos' },
  { key: 'cancelados', label: 'Cancelados' },
  { key: 'excluidos', label: 'Excluídos' },
  { key: 'purgados', label: 'Purgados (auditoria)' },
  { key: 'todos', label: 'Todos' },
];

const money = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export default function EnrollmentTitlesHealth() {
  const [view, setView] = useState('ativos');
  const [filters, setFilters] = useState({ year: '', class_group_id: '', student: '', status: '', kind: '', only_with_pix: false });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState([]);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [resetting, setResetting] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ view });
      if (filters.year) q.set('year', filters.year);
      if (filters.class_group_id) q.set('class_group_id', filters.class_group_id);
      if (filters.status) q.set('status', filters.status);
      if (filters.kind) q.set('kind', filters.kind);
      if (filters.only_with_pix) q.set('only_with_pix', 'true');
      const { data } = await api.get(`/enrollment/health/enrollment-titles?${q.toString()}`);
      setData(data); setSel([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao carregar títulos de matrícula');
    } finally { setLoading(false); }
  }, [view, filters]);

  useEffect(() => { load(); }, [load]);

  const rows = (data?.items || []).filter((i) =>
    !filters.student || (i.student_name || '').toLowerCase().includes(filters.student.toLowerCase()));

  const zerar = async (processId) => {
    const reason = window.prompt('Motivo para zerar o processo (obrigatório):');
    if (!reason) return;
    setResetting(processId);
    try {
      const { data } = await api.post(`/enrollment/processes/${processId}/reset`,
        { reason, purge_titles: true });
      toast.success(`Processo zerado · ${data.titles_purged} títulos removidos · ${data.payments_reversed || 0} pagamentos revertidos`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Falha ao zerar');
    } finally { setResetting(''); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="enrollment-titles-health">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-800">Matrículas & Rematrículas</h3>
          <p className="text-[11px] text-slate-500">Todos os títulos gerados por processo de matrícula — inclusive cancelados, excluídos e purgados.</p>
        </div>
        <div className="flex flex-wrap gap-1">
          {VIEWS.map((v) => (
            <Button key={v.key} size="sm" variant={view === v.key ? 'default' : 'outline'}
              onClick={() => setView(v.key)} data-testid={`eth-view-${v.key}`}>{v.label}</Button>
          ))}
          <Button size="sm" variant="outline" onClick={load} data-testid="eth-reload">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {data?.counts && view !== 'purgados' && (
        <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-5 lg:grid-cols-9" data-testid="eth-counts">
          {['total', 'pagos', 'abertos', 'vencidos', 'cancelados', 'excluidos', 'estornados', 'orfaos', 'com_pix'].map((k) => (
            <div key={k} className="rounded-lg border p-2">
              <p className="text-[10px] uppercase text-slate-400">{k}</p>
              <p className="text-lg font-bold">{data.counts[k] ?? 0}</p>
            </div>
          ))}
        </div>
      )}
      {data?.counts?.valor_total != null && view !== 'purgados' && (
        <p className="mb-3 text-xs text-slate-500" data-testid="eth-total-value">Valor total listado: <strong>{money(data.counts.valor_total)}</strong></p>
      )}

      <div className="mb-3 flex flex-wrap gap-2">
        <Input placeholder="Aluno" value={filters.student} onChange={(e) => setFilters((f) => ({ ...f, student: e.target.value }))} className="h-8 w-40 text-xs" data-testid="eth-filter-student" />
        <Input placeholder="Ano" value={filters.year} onChange={(e) => setFilters((f) => ({ ...f, year: e.target.value }))} className="h-8 w-24 text-xs" data-testid="eth-filter-year" />
        <select value={filters.kind} onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value }))}
          className="h-8 rounded-md border px-2 text-xs" data-testid="eth-filter-kind">
          <option value="">Tipo: todos</option>
          <option value="matricula">Matrícula/Rematrícula</option>
          <option value="mensalidade">Demais parcelas</option>
        </select>
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="h-8 rounded-md border px-2 text-xs" data-testid="eth-filter-status">
          <option value="">Status: todos</option>
          {['pendente', 'pago', 'vencido', 'cancelado'].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-1 text-xs text-slate-600">
          <input type="checkbox" checked={filters.only_with_pix}
            onChange={(e) => setFilters((f) => ({ ...f, only_with_pix: e.target.checked }))} data-testid="eth-filter-pix" /> só com PIX
        </label>
        {view !== 'purgados' && (
          <Button size="sm" variant="destructive" disabled={sel.length === 0} onClick={() => setPurgeOpen(true)} data-testid="eth-purge-selected">
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Excluir selecionados ({sel.length})
          </Button>
        )}
      </div>

      <div className="max-h-[460px] overflow-auto">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 bg-white text-[10px] uppercase text-slate-400">
            <tr>
              {view !== 'purgados' && <th className="w-8" />}
              <th className="text-left">Aluno</th><th className="text-left">Processo</th>
              <th className="text-left">Título</th><th className="text-left">Parcela</th>
              <th className="text-left">Venc.</th><th className="text-left">Valor</th>
              <th className="text-left">Status</th><th className="text-left">PIX</th>
              <th className="text-left">Gestão</th><th className="text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {view === 'purgados' && rows.map((i) => (
              <tr key={i.title_id} className="border-t border-slate-50" data-testid={`eth-purged-${i.title_id}`}>
                <td>{i.student_name || '—'}</td>
                <td className="text-[10px]">{i.enrollment_process_id?.slice(0, 8) || '—'}</td>
                <td>{i.category || '—'}</td><td>—</td>
                <td>{i.due_date || '—'}</td><td>{money(i.amount)}</td>
                <td><Badge variant="outline">purgado</Badge></td>
                <td>—</td>
                <td className="text-[10px] text-slate-400">só auditoria</td>
                <td className="text-right text-[10px] text-slate-500">{i.purge_reason} · {i.purged_by} · {String(i.purged_at).slice(0, 10)}</td>
              </tr>
            ))}
            {view !== 'purgados' && rows.map((i) => (
              <tr key={i.title_id} className="border-t border-slate-50" data-testid={`eth-row-${i.title_id}`}>
                <td>
                  <input type="checkbox" checked={sel.includes(i.title_id)}
                    onChange={() => setSel((p) => p.includes(i.title_id) ? p.filter((x) => x !== i.title_id) : [...p, i.title_id])}
                    data-testid={`eth-check-${i.title_id}`} />
                </td>
                <td className="font-medium">{i.student_name || '—'}</td>
                <td className="text-[10px]">{i.process_status || '—'}</td>
                <td>{i.description || '—'}</td>
                <td>{i.installment}</td>
                <td>{i.due_date}</td>
                <td>{money(i.amount)}</td>
                <td>
                  <Badge variant="outline">{i.status}</Badge>
                  {i.reversed && <span className="ml-1 text-[10px] text-amber-700">estornado</span>}
                  {i.orphan && <span className="ml-1 text-[10px] text-rose-700">órfão</span>}
                </td>
                <td>{i.has_pix ? 'sim' : '—'}</td>
                <td className="text-[10px]">{i.visible_in_titles ? 'visível' : 'oculto'}</td>
                <td className="text-right">
                  <div className="flex justify-end gap-1">
                    <a href={`/v2/aluno?student=${i.student_id}`} target="_blank" rel="noreferrer"
                      className="text-slate-400 hover:text-slate-700" data-testid={`eth-view-${i.title_id}`}>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                    <button onClick={() => { setSel([i.title_id]); setPurgeOpen(true); }}
                      className="text-rose-600 hover:text-rose-800" title="Excluir definitivamente"
                      data-testid={`eth-purge-${i.title_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    {i.process_id && (
                      <button onClick={() => zerar(i.process_id)} disabled={resetting === i.process_id}
                        className="text-amber-600 hover:text-amber-800" title="Zerar processo"
                        data-testid={`eth-reset-${i.title_id}`}>
                        {resetting === i.process_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={11} className="py-6 text-center text-slate-400">Nenhum título nesta visão.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {view === 'purgados' && (
        <p className="mt-3 flex items-center gap-2 text-[11px] text-slate-500" data-testid="eth-purged-note">
          <ShieldAlert className="h-3.5 w-3.5" /> Títulos purgados não existem mais operacionalmente, mas a evidência administrativa fica registrada aqui e em finance_events (TITLE_PURGED).
        </p>
      )}

      <PurgeBulkDialog open={purgeOpen} titleIds={sel} onClose={() => setPurgeOpen(false)} onDone={load} />
    </div>
  );
}
